import { useState, useEffect } from 'react';
import Layout from './Layout';
import { useAuth } from '../hooks/useAuth';
import { createAuthenticatedAxios, sessionManager } from '../utils/auth';
import '../styles.css';
import { API_URL } from '../config';

function Dashboard() {
  const { isAuthenticated, logout } = useAuth();
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({ answersCount: 0, customQuestionsCount: 0 });
  
  // Account management states
  const [newEmail, setNewEmail] = useState('');
  const [location, setLocation] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [actionStatus, setActionStatus] = useState('');
  
  // Question management states
  const [newQuestionText, setNewQuestionText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [answers, setAnswers] = useState([]);
  const [filteredAnswers, setFilteredAnswers] = useState([]);
  
  // Inline editing states
  const [editingItem, setEditingItem] = useState(null);
  const [editingText, setEditingText] = useState('');
  
  // Answer question states
  const [answeringQuestion, setAnsweringQuestion] = useState(null);
  const [newAnswerText, setNewAnswerText] = useState('');
  const [allQuestions, setAllQuestions] = useState([]);
  
  // AI Query states
  const [query, setQuery] = useState('');
  const [queryResponse, setQueryResponse] = useState('');
  const [queryHistory, setQueryHistory] = useState([]);
  
  // AI History states
  const [aiHistory, setAiHistory] = useState([]);
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [totalHistoryCount, setTotalHistoryCount] = useState(0);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [answersPerPage] = useState(10);

  useEffect(() => {
    if (isAuthenticated) {
      fetchInitialData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Reset to first page when search term changes
    setCurrentPage(1);
    
    // Filter answers based on search term - WITH NULL SAFETY
    if (searchTerm && searchTerm.trim()) {
      try {
        const searchLower = searchTerm.toLowerCase();
        const filtered = answers.filter(item => {
          // Safe null checking for both questions and answers
          const questionText = item.question_text ? item.question_text.toString().toLowerCase() : '';
          const answerText = item.answer_text ? item.answer_text.toString().toLowerCase() : '';
          return questionText.includes(searchLower) || answerText.includes(searchLower);
        });
        setFilteredAnswers(filtered);
      } catch (err) {
        console.error('Error filtering answers:', err);
        setFilteredAnswers([]);
      }
    } else {
      // Show all answers for both basic and premium users when no search term
      setFilteredAnswers(answers);
    }
  }, [searchTerm, answers, isPremium]);

  // Calculate statistics when answers change (only for display purposes, not overriding user stats)
  useEffect(() => {
    // This is now handled by the /api/user endpoint data
  }, [answers]);

  useEffect(() => {
    // Load AI history when user switches to history section
    if (activeSection === 'query-history' && isPremium && aiHistory.length === 0) {
      fetchAIHistory(true);
    }
  }, [activeSection, isPremium]);

  const fetchInitialData = async () => {
    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }

    setIsLoading(true);
    try {
      const axios = createAuthenticatedAxios(logout);
      
      const [userResponse, subscriptionResponse] = await Promise.all([
        axios.get('/api/user'),
        axios.get('/api/subscription/status'),
      ]);

      setUser(userResponse.data);
      setIsPremium(subscriptionResponse.data.isPremium);
      setIsAdmin(userResponse.data.is_admin || false);
      console.log('Dashboard: isPremium =', subscriptionResponse.data.isPremium);
      console.log('Dashboard: User subscription status =', subscriptionResponse.data);
      setNewEmail(userResponse.data.email);
      if (userResponse.data.location) setLocation(userResponse.data.location);
      
      // Set stats from user data
      setStats({
        answersCount: userResponse.data.answersCount || 0,
        customQuestionsCount: userResponse.data.customQuestionsCount || 0
      });

      // Fetch answers and all questions for all users (basic users can view, premium users can edit)
      try {
        const [answersResponse, allQuestionsResponse] = await Promise.all([
          axios.get('/api/answers'),
          axios.get('/api/questions')
        ]);
        
        // The backend now returns both questions and answers
        const items = (answersResponse.data || []).filter(item => 
          item && 
          typeof item === 'object' && 
          item.item_id
        );
        setAnswers(items);
        
        // Clean all questions data
        const rawAllQuestions = allQuestionsResponse.data || [];
        const cleanAllQuestions = rawAllQuestions.filter(question => 
          question && typeof question === 'object' && question.id
        );
        setAllQuestions(cleanAllQuestions);
      } catch (answersErr) {
        console.error('Error fetching answers:', answersErr);
        setAnswers([]);
        setAllQuestions([]);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const showStatus = (message, isError = false) => {
    setActionStatus(message);
    setTimeout(() => setActionStatus(''), 3000);
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    try {
      const axios = createAuthenticatedAxios(logout);
      await axios.put('/api/user/email', { newEmail });
      setUser({ ...user, email: newEmail });
      showStatus('Email updated successfully!');
    } catch (err) {
      showStatus('Failed to update email', true);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showStatus('Passwords do not match', true);
      return;
    }
    try {
      const axios = createAuthenticatedAxios(logout);
      await axios.post('/api/user/password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showStatus('Password updated successfully!');
    } catch (err) {
      showStatus('Failed to update password', true);
    }
  };

  const handleDownloadData = async () => {
    try {
      const axios = createAuthenticatedAxios(logout);
      const response = await axios.get('/api/answers');
      
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'true-devotion-data.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showStatus('Data downloaded successfully!');
    } catch (err) {
      showStatus('Failed to download data', true);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      const axios = createAuthenticatedAxios(logout);
      await axios.post('/api/user/subscription', {});
      setIsPremium(false);
      showStatus('Subscription cancelled successfully');
    } catch (err) {
      showStatus('Failed to cancel subscription', true);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This cannot be undone.')) return;
    
    try {
      const axios = createAuthenticatedAxios(logout);
      await axios.delete('/api/user');
      localStorage.removeItem('token');
      window.location.href = '/login';
    } catch (err) {
      showStatus('Failed to delete account', true);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    if (!newQuestionText.trim()) return;
    
    try {
      const axios = createAuthenticatedAxios(logout);
      await axios.post('/api/questions', { text: newQuestionText });
      setNewQuestionText('');
      showStatus('Question added successfully!');
      
      // Refresh the data to show the new question
      fetchInitialData();
    } catch (err) {
      showStatus('Failed to add question', true);
    }
  };

  const handleAIQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    setIsLoading(true);
    try {
      const axios = createAuthenticatedAxios(logout);
      const response = await axios.post('/api/grok/query', { question: query });
      setQueryResponse(response.data.encryptedResponse);
      setQueryHistory([{ query, response: response.data.encryptedResponse, timestamp: new Date() }, ...queryHistory]);
      setQuery('');
      showStatus('Query processed successfully!');
      // Refresh AI history to show the new query
      fetchAIHistory(true);
    } catch (err) {
      showStatus('Failed to process query', true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAIHistory = async (reset = false) => {
    if (!isPremium) return;
    
    setHistoryLoading(true);
    try {
      const axios = createAuthenticatedAxios(logout);
      const offset = reset ? 0 : historyPage * 20;
      const params = new URLSearchParams({
        limit: '20',
        offset: offset.toString()
      });
      
      if (historySearchTerm.trim()) {
        params.append('search', historySearchTerm.trim());
      }
      
      const response = await axios.get(`/api/grok/history?${params}`);
      
      if (reset) {
        setAiHistory(response.data.conversations);
        setHistoryPage(0);
      } else {
        setAiHistory(prev => [...prev, ...response.data.conversations]);
      }
      
      setHasMoreHistory(response.data.hasMore);
      setTotalHistoryCount(response.data.total);
    } catch (err) {
      console.error('Failed to fetch AI history:', err);
      showStatus('Failed to load AI history', true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistorySearch = async (e) => {
    e.preventDefault();
    setHistoryPage(0);
    await fetchAIHistory(true);
  };

  const loadMoreHistory = async () => {
    if (!hasMoreHistory || historyLoading) return;
    setHistoryPage(prev => prev + 1);
    await fetchAIHistory(false);
  };

  const handleDeleteAnswer = async (answerId) => {
    if (!window.confirm('Are you sure you want to delete this answer?')) return;
    
    try {
      const axios = createAuthenticatedAxios(logout);
      await axios.delete(`/api/answer/${answerId}`);
      setAnswers(answers.filter(item => !(item.item_id === answerId && item.type === 'answer')));
      showStatus('Answer deleted successfully!');
    } catch (err) {
      showStatus('Failed to delete answer', true);
    }
  };

  // New handler functions for questions and answers - INLINE EDITING
  const handleStartEdit = (item, field) => {
    setEditingItem(`${item.type}-${item.item_id}-${field}`);
    if (field === 'question') {
      setEditingText(item.question_text || '');
    } else {
      setEditingText(item.answer_text || '');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditingText('');
  };

  // Pagination functions
  const indexOfLastAnswer = currentPage * answersPerPage;
  const indexOfFirstAnswer = indexOfLastAnswer - answersPerPage;
  const currentAnswers = filteredAnswers.slice(indexOfFirstAnswer, indexOfLastAnswer);
  const totalPages = Math.ceil(filteredAnswers.length / answersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSaveEdit = async (item, field) => {
    if (!editingText.trim()) {
      showStatus('Text cannot be empty', true);
      return;
    }

    try {
      const axios = createAuthenticatedAxios(logout);
      
      if (field === 'question') {
        await axios.put(`/api/questions/${item.question_id}`, { text: editingText.trim() });
        
        // Update local state
        setAnswers(answers.map(a => 
          a.item_id === item.item_id && a.type === 'question'
            ? { ...a, question_text: editingText.trim() }
            : a
        ));
        showStatus('Question updated successfully!');
      } else {
        await axios.put(`/api/answer/${item.item_id}`, { text: editingText.trim() });
        
        // Update local state
        setAnswers(answers.map(a => 
          a.item_id === item.item_id && a.type === 'answer'
            ? { ...a, answer_text: editingText.trim() }
            : a
        ));
        showStatus('Answer updated successfully!');
      }
      
      setEditingItem(null);
      setEditingText('');
    } catch (err) {
      showStatus(`Failed to update ${field}`, true);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This cannot be undone.')) return;
    
    try {
      const axios = createAuthenticatedAxios(logout);
      console.log('Deleting question with ID:', questionId);
      
      const response = await axios.delete(`/api/questions/${questionId}`);
      
      console.log('Delete response:', response.data);
      
      // Update local state - check both item_id and question_id
      setAnswers(answers.filter(item => 
        !(item.type === 'question' && (item.item_id === questionId || item.question_id === questionId))
      ));
      
      showStatus('Question deleted successfully!');
      
      // Refresh data to ensure consistency
      setTimeout(() => {
        fetchInitialData();
      }, 1000);
    } catch (err) {
      console.error('Delete question error:', err);
      showStatus('Failed to delete question: ' + (err.response?.data?.error || err.message), true);
    }
  };

  const handleAnswerQuestion = (question) => {
    setAnsweringQuestion(question.id);
    setNewAnswerText('');
  };

  const handleSaveAnswer = async () => {
    if (!newAnswerText.trim()) {
      showStatus('Please enter an answer', true);
      return;
    }

    try {
      const axios = createAuthenticatedAxios(logout);
      await axios.post('/api/answer', { questionId: answeringQuestion, text: newAnswerText });
      
      // Refresh the data to show the new answer
      await fetchInitialData();
      
      setAnsweringQuestion(null);
      setNewAnswerText('');
      showStatus('Answer saved successfully!');
    } catch (err) {
      showStatus('Failed to save answer: ' + (err.response?.data?.error || err.message), true);
    }
  };

  const handleCancelAnswer = () => {
    setAnsweringQuestion(null);
    setNewAnswerText('');
  };

  if (isLoading) {
    return <div className="spinner">Loading...</div>;
  }

  return (
    <Layout>
      <div className="dashboard-container">

      <h1>Dashboard</h1>
      
      {user && (
        <div className="user-info" style={{ marginBottom: '20px' }}>
          <p className="dashboard-greeting">Welcome, {user.email}!</p>
          <div style={{ display: 'flex', gap: '30px', marginTop: '10px' }}>
            <div>
              <strong>Account:</strong> {isPremium ? 'Premium' : 'Basic'}
            </div>
            {isPremium && (
              <>
                <div>
                  <strong>Questions Answered:</strong> {stats.answersCount}
                </div>
                <div>
                  <strong>Custom Questions Created:</strong> {stats.customQuestionsCount}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {actionStatus && (
        <div className={`status-message ${actionStatus.includes('Failed') ? 'error' : 'success'}`}>
          {actionStatus}
        </div>
      )}

      <div className="dashboard-sections">
        {activeSection === 'overview' ? (
          <div className="dashboard-hub-list">
            <button className="dashboard-hub-item" onClick={() => setActiveSection('account')}>
              <span>Account Settings</span>
              <span className="chevron">&rsaquo;</span>
            </button>
            <button className="dashboard-hub-item" onClick={() => setActiveSection('search')}>
              <span>{isPremium ? 'Search Answers' : 'My Answers'}</span>
              <span className="chevron">&rsaquo;</span>
            </button>
            {isPremium && (
              <>
                <button className="dashboard-hub-item" onClick={() => setActiveSection('ai-query')}>
                  <span>AI Assistant</span>
                  <span className="chevron">&rsaquo;</span>
                </button>
                <button className="dashboard-hub-item" onClick={() => setActiveSection('questions')}>
                  <span>Add Questions</span>
                  <span className="chevron">&rsaquo;</span>
                </button>
              </>
            )}
            {isAdmin && (
              <button className="dashboard-hub-item" onClick={() => setActiveSection('admin')}>
                <span>Admin Dashboard</span>
                <span className="chevron">&rsaquo;</span>
              </button>
            )}
          </div>
        ) : (
          <div className="dashboard-section-header">
            <button className="dashboard-back-btn" onClick={() => setActiveSection('overview')}>
              &lsaquo; Dashboard
            </button>
            <span className="dashboard-section-title">
              {({
                account: 'Account Settings',
                search: isPremium ? 'Search Answers' : 'My Answers',
                'ai-query': 'AI Assistant',
                'query-history': 'AI Chat History',
                questions: 'Add Questions',
                admin: 'Admin Dashboard',
              })[activeSection]}
            </span>
          </div>
        )}

        <div className="section-content">
          {activeSection === 'overview' && (
            <div>
              <div className="overview-cards" style={{
                display: 'grid',
                gap: '20px',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                marginTop: '20px'
              }}>
                {!isPremium && (
                  <div className="card" style={{
                    padding: '24px',
                    border: '1px solid var(--color-border-light)',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                  }}>
                    <h3 style={{ marginBottom: '16px', color: 'var(--color-text-medium)' }}>Upgrade to Premium</h3>
                    <p style={{ marginBottom: '20px', color: 'var(--color-text-muted)', lineHeight: '1.5' }}>
                      Get access to AI Assistant, custom questions, and advanced search features
                    </p>
                    <button 
                      onClick={() => window.location.href = '/upgrade'}
                      style={{
                        padding: '14px 28px',
                        backgroundColor: 'var(--color-success)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        width: '100%',
                        maxWidth: '300px',
                        margin: '0 auto',
                        display: 'block',
                        transition: 'background-color 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = 'var(--color-success-hover)'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'var(--color-success)'}
                    >
                      Upgrade Now
                    </button>
                  </div>
                )}
                
                <div className="card" style={{
                  padding: '24px',
                  border: '1px solid var(--color-border-light)',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  textAlign: 'center'
                }}>
                  <h3 style={{ marginBottom: '16px', color: 'var(--color-text-medium)' }}>Data & Privacy</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '0' }}>
                    <button 
                      onClick={handleDownloadData}
                      style={{
                        padding: 'var(--btn-pad-action)',
                        backgroundColor: 'var(--color-teal)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-pill)',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '500',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      Download My Data
                    </button>
                    <button 
                      onClick={handleDeleteAccount} 
                      className="danger-button"
                      style={{
                        padding: 'var(--btn-pad-action)',
                        backgroundColor: 'var(--color-danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-pill)',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '500',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'account' && (
            <div>
              <h2>Account Settings</h2>
              
              <div className="settings-section">
                <h3>Your Location</h3>
                <p style={{ color: '#666', marginTop: 0, fontSize: '16px' }}>Helps the AI find nearby restaurants, venues, and experiences when you ask for suggestions.</p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const axios = createAuthenticatedAxios(logout);
                    await axios.put('/api/user/location', { location });
                    showStatus('Location saved!');
                  } catch (err) {
                    showStatus('Failed to save location', true);
                  }
                }} style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Louisville, KY or 40241"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '6px',
                      fontSize: '16px',
                      marginBottom: '12px'
                    }}
                  />
                  <button type="submit" className="btn-form">Save Location</button>
                </form>
              </div>

              <div className="settings-section">
                <h3>Update Email</h3>
                <form onSubmit={handleEmailUpdate} style={{ marginBottom: '20px' }}>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="New email address"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '6px',
                      fontSize: '16px',
                      marginBottom: '12px'
                    }}
                  />
                  <button 
                    type="submit"
                    className="btn-form"
                  >
                    Update Email
                  </button>
                </form>
              </div>

              <div className="settings-section">
                <h3>Change Password</h3>
                <form onSubmit={handlePasswordUpdate} style={{ marginBottom: '20px' }}>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '6px',
                      fontSize: '16px',
                      marginBottom: '12px'
                    }}
                  />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '6px',
                      fontSize: '16px',
                      marginBottom: '12px'
                    }}
                  />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '6px',
                      fontSize: '16px',
                      marginBottom: '12px'
                    }}
                  />
                  <button 
                    type="submit"
                    className="btn-form"
                  >
                    Update Password
                  </button>
                </form>
              </div>

              {isPremium && (
                <div className="settings-section">
                  <h3>Subscription</h3>
                  <p>You have an active Premium subscription.</p>
                  <button 
                    onClick={handleCancelSubscription}
                    className="btn-form-danger"
                  >
                    Cancel Subscription
                  </button>
                </div>
              )}
            </div>
          )}

          {isPremium && activeSection === 'ai-query' && (
            <div>
              <h2 style={{ textAlign: 'left' }}>AI Assistant</h2>

              {/* Response appears first, above the form */}
              {queryResponse && (
                <div className="ai-response" style={{
                  backgroundColor: '#f0f7ff',
                  border: '1px solid #b8d9f8',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  textAlign: 'left'
                }}>
                  <h3 style={{ marginTop: 0, color: '#1a5276' }}>Response</h3>
                  <p style={{ lineHeight: '1.8', margin: 0, whiteSpace: 'pre-wrap' }}>{queryResponse}</p>
                </div>
              )}

              <form onSubmit={handleAIQuery} style={{ padding: '0' }}>
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g., 'Help me plan a date night she'd enjoy' or 'What gift would mean the most to her?'"
                  rows="3"
                  style={{ width: '100%', marginBottom: '10px', boxSizing: 'border-box', fontSize: '16px', padding: '10px' }}
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: isLoading ? 'var(--color-text-muted)' : 'var(--color-info)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  {isLoading ? 'Thinking...' : 'Ask'}
                </button>
              </form>

              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={() => setActiveSection('query-history')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--color-text-muted)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  View Conversation History
                </button>
              </div>
            </div>
          )}

          {isPremium && activeSection === 'query-history' && (
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ textAlign: 'left' }}>AI Chat History</h2>
                <button 
                  onClick={() => setActiveSection('ai-query')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--color-info)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  ← Ask New Question
                </button>
              </div>
              
              {/* Search functionality */}
              <div style={{ marginBottom: '20px' }}>
                <form onSubmit={handleHistorySearch} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '0' }}>
                  <input
                    type="text"
                    value={historySearchTerm}
                    onChange={(e) => setHistorySearchTerm(e.target.value)}
                    placeholder="Search your AI conversations..."
                    style={{ 
                      flex: 1, 
                      padding: '10px', 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button 
                    type="submit"
                    disabled={historyLoading}
                    className="btn-form-success"
                  >
                    {historyLoading ? 'Searching...' : 'Search'}
                  </button>
                  {historySearchTerm && (
                    <button 
                      type="button"
                      onClick={() => {
                        setHistorySearchTerm('');
                        fetchAIHistory(true);
                      }}
                      style={{
                        padding: '10px 15px',
                        backgroundColor: 'var(--color-text-muted)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      Clear
                    </button>
                  )}
                </form>
              </div>

              {/* Results info */}
              {totalHistoryCount > 0 && (
                <div style={{ marginBottom: '15px', color: '#666', fontSize: '16px' }}>
                  {historySearchTerm ? (
                    <span>Found <strong>{totalHistoryCount}</strong> conversation{totalHistoryCount !== 1 ? 's' : ''} matching "{historySearchTerm}"</span>
                  ) : (
                    <span>Showing <strong>{aiHistory.length}</strong> of <strong>{totalHistoryCount}</strong> total conversations</span>
                  )}
                </div>
              )}
              
              {/* Conversation list */}
              {historyLoading && aiHistory.length === 0 ? (
                <div style={{ textAlign: 'left', padding: '40px', color: '#666' }}>
                  Loading your AI conversation history...
                </div>
              ) : aiHistory.length > 0 ? (
                <div className="ai-history">
                  {aiHistory.map((conversation) => (
                    <div key={conversation.id} className="conversation-item" style={{
                      backgroundColor: '#f8f9fa',
                      padding: '20px',
                      marginBottom: '20px',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border-light)',
                      position: 'relative'
                    }}>
                      <div style={{ 
                        position: 'absolute', 
                        top: '10px', 
                        right: '10px',
                        backgroundColor: '#e9ecef',
                        color: 'var(--color-text-medium)',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        zIndex: 10
                      }}>
                        PAST RESPONSE
                      </div>
                      
                      <div style={{ marginTop: '30px', marginBottom: '15px' }}>
                        <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-text-medium)', fontSize: '16px' }}>
                          <span style={{ color: 'var(--color-info)' }}>Q:</span> {conversation.query_text}
                        </h4>
                        <p style={{ 
                          margin: '0', 
                          lineHeight: '1.6', 
                          color: '#212529',
                          backgroundColor: '#ffffff',
                          padding: '15px',
                          borderRadius: '6px',
                          border: '1px solid #e9ecef'
                        }}>
                          <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>A:</span> {conversation.response_text}
                        </p>
                      </div>
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        borderTop: '1px solid var(--color-border-light)',
                        paddingTop: '10px',
                        marginTop: '15px'
                      }}>
                        <small style={{ color: 'var(--color-text-muted)' }}>
                          {new Date(conversation.timestamp).toLocaleString()}
                        </small>
                        <button
                          onClick={() => {
                            setQuery(conversation.query_text);
                            setActiveSection('ai-query');
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: 'var(--color-teal)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Ask Similar Question
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {/* Load more button */}
                  {hasMoreHistory && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <button
                        onClick={loadMoreHistory}
                        disabled={historyLoading}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: historyLoading ? 'var(--color-text-muted)' : 'var(--color-info)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: historyLoading ? 'not-allowed' : 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        {historyLoading ? 'Loading...' : 'Load More Conversations'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'left', 
                  padding: '40px', 
                  color: '#666',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border-light)'
                }}>
                  <h3 style={{ color: 'var(--color-text-medium)', marginBottom: '10px', textAlign: 'left' }}>No AI conversations found</h3>
                  <p style={{ marginBottom: '20px', textAlign: 'left' }}>
                    {historySearchTerm 
                      ? `No conversations match "${historySearchTerm}". Try a different search term.`
                      : "You haven't asked the AI any questions yet. Start by asking about gift ideas, date suggestions, or relationship insights!"
                    }
                  </p>
                  <button
                    onClick={() => setActiveSection('ai-query')}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'var(--color-info)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Ask Your First Question
                  </button>
                </div>
              )}
            </div>
          )}

          {activeSection === 'search' && (
            <div style={{ textAlign: 'left' }}>
              <h2 style={{ textAlign: 'left' }}>{isPremium ? 'Search Your Questions & Answers' : 'My Questions & Answers'}</h2>
              
              {/* Search box */}
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search your questions and answers..."
                style={{ width: '100%', marginBottom: '20px', padding: '10px' }}
              />
              
              {/* Results count and pagination info */}
              {filteredAnswers.length > 0 && (
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{filteredAnswers.length}</strong> {filteredAnswers.length === 1 ? 'answer' : 'answers'} found
                    {!searchTerm && (
                      <span style={{ color: '#666', marginLeft: '10px' }}>
                        (showing all your answers)
                      </span>
                    )}
                  </div>
                  {totalPages > 1 && (
                    <div>
                      Page {currentPage} of {totalPages}
                    </div>
                  )}
                </div>
              )}
              
              {/* Unanswered Questions Section - REMOVED TO PREVENT DUPLICATES */}
              
              <div className="search-results">
                {(() => {
                  // Combine answered items with unanswered standard questions
                  const answeredQuestionIds = new Set(answers.filter(item => item.type === 'answer').map(item => item.question_id));
                  const unansweredStandardQuestions = allQuestions.filter(question => 
                    !answeredQuestionIds.has(question.id) && 
                    question.created_by_user_id !== user?.id // Don't include user's own questions here
                  );
                  
                  
                  const filteredUnansweredStandard = searchTerm ? 
                    unansweredStandardQuestions.filter(question => {
                      const questionText = question.text ? question.text.toString().toLowerCase() : '';
                      return questionText.includes(searchTerm.toLowerCase());
                    }) : 
                    []; // Only show unanswered questions when searching
                  
                  // Convert unanswered standard questions to the same format as other items
                  const unansweredItems = filteredUnansweredStandard.map(question => ({
                    type: 'unanswered_question',
                    item_id: question.id,
                    question_id: question.id,
                    question_text: question.text,
                    answer_text: null,
                    created_at: question.created_at,
                    is_custom_question: false
                  }));
                  
                  // Combine current answers with unanswered standard questions.
                  // Hide custom-question stubs that already have an answer row
                  // (otherwise they still show as "not answered" after you answer from search).
                  const allItems = [
                    ...currentAnswers.filter(item =>
                      !(item.type === 'question' && answeredQuestionIds.has(item.question_id))
                    ),
                    ...unansweredItems
                  ];
                  
                  // Debug logging to see what's happening
                  console.log('Search term:', searchTerm);
                  console.log('Current answers count:', currentAnswers.length);
                  console.log('Unanswered items count:', unansweredItems.length);
                  console.log('All items count:', allItems.length);
                  console.log('Current answers:', currentAnswers.map(item => ({ 
                    question_id: item.question_id, 
                    question_text: item.question_text?.substring(0, 50) + '...',
                    has_answer: !!item.answer_text,
                    answer_text: item.answer_text?.substring(0, 30) + '...',
                    type: item.type,
                    item_id: item.item_id
                  })));
                  console.log('Unanswered items:', unansweredItems.map(item => ({ 
                    question_id: item.question_id, 
                    question_text: item.question_text?.substring(0, 50) + '...',
                    type: item.type,
                    item_id: item.item_id
                  })));
                  
                  // Check for the specific business question
                  const businessAnswers = currentAnswers.filter(item => 
                    item.question_text && item.question_text.toLowerCase().includes('business')
                  );
                  const businessUnanswered = unansweredItems.filter(item => 
                    item.question_text && item.question_text.toLowerCase().includes('business')
                  );
                  console.log('Business answers:', businessAnswers);
                  console.log('Business unanswered:', businessUnanswered);
                  
                  return allItems.length > 0 ? (
                    allItems.map((item) => (
                    <div key={`${item.type}-${item.item_id}`} className="search-item" style={{
                      border: '1px solid #ddd',
                      padding: '20px',
                      marginBottom: '20px',
                      borderRadius: '8px',
                      backgroundColor: item.type === 'question' ? '#f0f8ff' : item.type === 'unanswered_question' ? '#fff3cd' : '#f9f9f9'
                    }}>
                      {/* Question Text */}
                      <div style={{ marginBottom: '10px' }}>
                        {editingItem === `${item.type}-${item.item_id}-question` ? (
                          <div>
                            <textarea
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '2px solid var(--color-info)',
                                borderRadius: '4px',
                                minHeight: '60px',
                                fontFamily: 'inherit',
                                fontSize: '16px'
                              }}
                              autoFocus
                            />
                            <div style={{ marginTop: '8px' }}>
                              <button
                                onClick={() => handleSaveEdit(item, 'question')}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: 'var(--color-success)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  marginRight: '8px'
                                }}
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: 'var(--color-text-muted)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <h4 style={{ color: '#333', marginBottom: '5px', cursor: item.is_custom_question ? 'pointer' : 'default' }}
                              onClick={() => item.is_custom_question && handleStartEdit(item, 'question')}>
                            {item.question_text || 'No question text'}
                            {item.is_custom_question && (
                              <span style={{ 
                                marginLeft: '10px', 
                                fontSize: '12px', 
                                backgroundColor: 'var(--color-info)', 
                                color: 'white', 
                                padding: '2px 6px', 
                                borderRadius: '3px' 
                              }}>
                                Custom Question
                              </span>
                            )}
                            {item.is_custom_question && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '12px', 
                                color: 'var(--color-text-muted)',
                                fontStyle: 'italic'
                              }}>
                                (tap to edit)
                              </span>
                            )}
                          </h4>
                        )}
                      </div>
                      
                      {/* Answer Text (if exists) */}
                      {item.answer_text && (
                        <div style={{ marginBottom: '15px' }}>
                          {editingItem === `${item.type}-${item.item_id}-answer` ? (
                            <div>
                              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Answer:</label>
                              <textarea
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '2px solid var(--color-info)',
                                  borderRadius: '4px',
                                  minHeight: '100px',
                                  fontFamily: 'inherit',
                                  fontSize: '16px'
                                }}
                                autoFocus
                              />
                              <div style={{ marginTop: '8px' }}>
                                <button
                                  onClick={() => handleSaveEdit(item, 'answer')}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'var(--color-success)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '8px'
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: 'var(--color-text-muted)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p style={{ lineHeight: '1.5', cursor: 'pointer' }}
                               onClick={() => handleStartEdit(item, 'answer')}>
                              <strong>Answer:</strong> {item.answer_text}
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '12px', 
                                color: 'var(--color-text-muted)',
                                fontStyle: 'italic'
                              }}>
                                (tap to edit)
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Answer from search — custom unanswered, standard unanswered, or library unanswered */}
                      {((item.type === 'question' && !item.answer_text) || item.type === 'unanswered_question') && (
                        <div style={{ marginBottom: '15px' }}>
                          {answeringQuestion === item.question_id ? (
                            <div>
                              <textarea
                                value={newAnswerText}
                                onChange={(e) => setNewAnswerText(e.target.value)}
                                placeholder="Enter her answer..."
                                rows="4"
                                style={{ 
                                  width: '100%', 
                                  padding: '10px',
                                  marginBottom: '10px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                              <div>
                                <button 
                                  onClick={handleSaveAnswer} 
                                  style={{ 
                                    marginRight: '10px', 
                                    backgroundColor: 'var(--color-success)', 
                                    color: 'white',
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Save Answer
                                </button>
                                <button 
                                  onClick={handleCancelAnswer}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: 'var(--color-text-muted)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p style={{ 
                                marginBottom: '15px', 
                                fontStyle: 'italic', 
                                color: '#666' 
                              }}>
                                Not answered yet — you can answer it here, or wait for it in rotation
                              </p>
                              <button 
                                onClick={() => handleAnswerQuestion({ id: item.question_id })}
                                style={{ 
                                  backgroundColor: 'var(--color-info)', 
                                  color: 'white',
                                  padding: '8px 16px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                Answer Question
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Metadata */}
                      <div style={{ marginBottom: '15px' }}>
                        <small style={{ color: '#666' }}>
                          {item.type === 'answer' ? 'Answered' : 'Created'}: {
                            item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Unknown date'
                          }
                        </small>
                      </div>
                      
                      {/* Action Buttons - For all users on their own content */}
                      {item.type !== 'unanswered_question' && (item.type === 'answer' || item.is_custom_question) ? (
                        <div className="item-actions" style={{ display: 'flex', gap: '10px' }}>
                          {/* Delete Button */}
                          <button 
                            onClick={() => {
                              if (item.type === 'answer') {
                                handleDeleteAnswer(item.item_id);
                              } else {
                                // For questions, use question_id if available, otherwise item_id
                                const questionId = item.question_id || item.item_id;
                                handleDeleteQuestion(questionId);
                              }
                            }}
                            style={{ 
                              padding: '6px 12px',
                              backgroundColor: 'var(--color-danger)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Delete {item.type === 'answer' ? 'Answer' : 'Question'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <p>{searchTerm ? `No results found for "${searchTerm}"` : 'No answers found'}</p>
                );
                })()}
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginTop: '30px' 
                }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: currentPage === 1 ? 'var(--color-text-muted)' : 'var(--color-info)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: currentPage === pageNumber ? 'var(--color-info)' : '#f8f9fa',
                        color: currentPage === pageNumber ? 'white' : 'var(--color-text-medium)',
                        border: '1px solid var(--color-border-light)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        minWidth: '40px'
                      }}
                    >
                      {pageNumber}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: currentPage === totalPages ? 'var(--color-text-muted)' : 'var(--color-info)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}

          {isPremium && activeSection === 'questions' && (
            <div>
              <h2>Add Custom Questions</h2>
              <p>Add your own questions to the rotation system.</p>
              
              <form onSubmit={handleAddQuestion}>
                <textarea
                  value={newQuestionText}
                  onChange={(e) => setNewQuestionText(e.target.value)}
                  placeholder="Enter your custom question..."
                  rows="3"
                  style={{ width: '100%', marginBottom: '10px' }}
                  required
                />
                <button 
                  type="submit"
                  className="btn-form"
                >
                  Add Question
                </button>
              </form>
            </div>
          )}

          {isAdmin && activeSection === 'admin' && (
            <div>
              <h2>Admin Dashboard</h2>
              <p>Access administrative functions and user management.</p>
              
              <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <button 
                  onClick={() => window.location.href = '/admin'}
                  style={{
                    padding: '16px 32px',
                    backgroundColor: 'var(--color-info)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    fontWeight: '500',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = 'var(--color-info-hover)'}
                  onMouseOut={(e) => e.target.style.backgroundColor = 'var(--color-info)'}
                >
                  Go to Admin Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </Layout>
  );
}

export default Dashboard;